'use client';

import Image from 'next/image';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import customRenderers from '@/components/block-editor';
import LinkNoPrefetch from '@/components/link-noprefetch';
import {
    IconBook,
    IconBrandGithub,
    IconExternalLink,
    IconArrowRight,
    IconMessageCircle
} from '@tabler/icons-react';

function ActionButton({ href, children, variant = 'primary' }) {
    if (!href) return null;

    const base =
        'inline-flex items-center gap-2 rounded-lg border-2 border-solid px-5 py-2.5 text-sm font-medium transition-colors duration-300 no-underline';
    const styles = {
        primary: 'border-blue-700 bg-blue-700 text-white hover:bg-white hover:text-blue-700',
        outlined: 'border-blue-700 bg-transparent text-blue-700 hover:bg-blue-700 hover:text-white'
    };

    return (
        <LinkNoPrefetch href={href} className={`${base} ${styles[variant] || styles.outlined}`}>
            {children}
        </LinkNoPrefetch>
    );
}

function SidebarCard({ title, children }) {
    return (
        <div className="rounded-lg border border-gray-200 p-6">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {title}
            </h3>
            {children}
        </div>
    );
}

function ResourceLink({ href, icon: Icon, label }) {
    if (!href) return null;

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-1.5 text-sm text-blue-700 hover:text-blue-800 hover:underline">
            <Icon size={16} />
            {label}
        </a>
    );
}

export default function IntegrationDetail({ integration }) {
    const {
        title,
        tagline,
        badge,
        category,
        identifier,
        description,
        setupSummary,
        docsUrl,
        sourceUrl,
        partnerUrl,
        logo
    } = integration;

    return (
        <>
            {/* Header */}
            <div className="background-crossline mb-10 p-8 pt-12 lg:p-16 lg:px-32">
                <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
                    {logo ? (
                        <div className="mb-4 flex w-full items-center justify-center px-6">
                            <div className="relative h-24 w-4/5">
                                <Image
                                    src={`/dA/${identifier}/image`}
                                    alt={title}
                                    fill
                                    sizes="200px"
                                    className="object-contain"
                                />
                            </div>
                        </div>
                    ) : (
                        <h1 className="text-4xl font-bold">{title}</h1>
                    )}

                    {tagline && <p className="text-lg text-gray-600">{tagline}</p>}
                    <div className="mt-8 flex flex-wrap gap-3">
                        <ActionButton href={docsUrl} variant="primary">
                            <IconBook size={18} />
                            Read the setup guide
                        </ActionButton>
                        <ActionButton href={sourceUrl} variant="outlined">
                            <IconBrandGithub size={18} />
                            View source on GitHub
                        </ActionButton>
                        <ActionButton href={partnerUrl} variant="outlined">
                            <IconExternalLink size={18} />
                            Partner site
                        </ActionButton>
                    </div>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="container mb-24 grid grid-cols-6 gap-10">
                <div className="col-span-4">
                    {description && (
                        <section className="mb-10">
                            <h2 className="mb-4 text-2xl font-bold">What it does</h2>
                            <div className="prose prose-sm max-w-none prose-h2:text-2xl prose-h3:text-xl">
                                <DotBlockEditor
                                    blocks={description}
                                    customRenderers={customRenderers}
                                />
                            </div>
                        </section>
                    )}

                    {setupSummary && (
                        <section>
                            <h2 className="mb-4 text-2xl font-bold">How to set it up</h2>
                            <div className="prose prose-sm max-w-none prose-h2:text-2xl prose-h3:text-xl">
                                <DotBlockEditor
                                    blocks={setupSummary}
                                    customRenderers={customRenderers}
                                />
                            </div>
                            {docsUrl && (
                                <LinkNoPrefetch
                                    href={docsUrl}
                                    className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline">
                                    Read the full setup guide
                                    <IconArrowRight size={16} />
                                </LinkNoPrefetch>
                            )}
                        </section>
                    )}
                </div>

                <aside className="col-span-2 flex flex-col gap-6">
                    <SidebarCard title="Details">
                        <dl className="space-y-3 text-sm">
                            {badge && (
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Type</dt>
                                    <dd className="font-medium text-gray-900">{badge}</dd>
                                </div>
                            )}
                            {category && (
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Category</dt>
                                    <dd className="font-medium text-gray-900">{category}</dd>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <dt className="text-gray-500">dotCMS version</dt>
                                <dd className="font-medium text-gray-900">All versions</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Pricing</dt>
                                <dd className="font-medium text-gray-900">Included</dd>
                            </div>
                        </dl>
                    </SidebarCard>

                    {(docsUrl || sourceUrl || partnerUrl) && (
                        <SidebarCard title="Resources">
                            <div className="flex flex-col gap-1">
                                <ResourceLink
                                    href={docsUrl}
                                    icon={IconBook}
                                    label="Documentation"
                                />
                                <ResourceLink
                                    href={sourceUrl}
                                    icon={IconBrandGithub}
                                    label="Source code"
                                />
                                <ResourceLink
                                    href={partnerUrl}
                                    icon={IconExternalLink}
                                    label="Partner website"
                                />
                            </div>
                        </SidebarCard>
                    )}

                    <SidebarCard title="Need help?">
                        <p className="mb-4 text-sm text-gray-600">
                            Have questions about this integration? Our team is here to help you get
                            started.
                        </p>
                        <ActionButton href="/contact-us" variant="primary">
                            <IconMessageCircle size={16} />
                            Talk to an expert
                        </ActionButton>
                    </SidebarCard>
                </aside>
            </div>
        </>
    );
}
